import { watch } from "vue"
import { NavigationGuard } from "vue-router"

import router from "@/router"
import { useUserStore } from "@/store/user-store"
import { useSubscriptionStore } from "@/store/subscription-store"
import { useCompanyStore } from "@/store/company-store"
import { createEnsureEmbedded, createEnsureNotEmbedded, ensureEmbedAuthenticated } from "@common/router/embed-guards"

export const ensureIsAnonymous: NavigationGuard = (_to, _from, _next) => {
  const userStore = useUserStore()
  if (userStore.isLoggedIn) {
    router.push({ name: "home" }).then()
    return false
  }

  return true
}

export const ensureIsAuthenticated: NavigationGuard = (to, _from, _next) => {
  const isLoggedIn = useUserStore().isLoggedIn
  if (!isLoggedIn) {
    router.push({ name: "login", query: { redirectedFromPath: to.fullPath } }).then()
    return false
  }

  return true
}

function waitForCompanyContextResolution(): Promise<void> {
  const companyStore = useCompanyStore()
  if (companyStore.contextStatus !== "initializing" && companyStore.contextStatus !== "switching") {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    const stop = watch(
      () => companyStore.contextStatus,
      (contextStatus) => {
        if (contextStatus === "initializing" || contextStatus === "switching") return
        stop()
        resolve()
      }
    )
  })
}

export const ensureIsCompanyOnboarded: NavigationGuard = async (_to, _from, _next) => {
  await waitForCompanyContextResolution()
  if (useUserStore().isVitableAdmin) return true

  const companyStore = useCompanyStore()
  if (companyStore.contextStatus !== "ready") return true

  const isCompanyOnboarded = companyStore.companyOnboardedIn
  if (!isCompanyOnboarded) {
    router.push({ name: "onboarding" }).then()
    return false
  }

  return true
}

export const ensureIsCompanyNotOnboarded: NavigationGuard = async (_to, _from, _next) => {
  await waitForCompanyContextResolution()
  const companyStore = useCompanyStore()
  if (companyStore.contextStatus !== "ready") return true

  const isCompanyOnboarded = companyStore.companyOnboardedIn
  if (isCompanyOnboarded) {
    router.push({ name: "home" }).then()
    return false
  }

  return true
}

export const ensureIsCompanyAdminOnboarded: NavigationGuard = (_to, _from, _next) => {
  const userStore = useUserStore()
  if (userStore.isVitableAdmin) return true

  const isOnboarded = userStore.companyAdmin?.onboarded_in
  if (!isOnboarded) {
    router.push({ name: "activate" }).then()
    return false
  }

  return true
}

export const ensureIsCompanyAdminNotOnboarded: NavigationGuard = (_to, _from, _next) => {
  const userStore = useUserStore()
  const isOnboarded = userStore.companyAdmin?.onboarded_in
  if (isOnboarded) {
    router.push({ name: "home" }).then()
    return false
  }

  return true
}

export const ensureHasActiveOrUpcomingBenefitsGuardian: NavigationGuard = (_to, _from, _next) => {
  const hasBenefits = useSubscriptionStore().hasBenefitsIn
  if (!hasBenefits) {
    router.push({ name: "home" }).then()
    return false
  }

  return true
}

export const ensureHasLabOrderingFeatureGuardian: NavigationGuard = (_to, _from, _next) => {
  const hasLabOrderingFeature = useSubscriptionStore().hasLabOrderingFeature
  if (!hasLabOrderingFeature) {
    router.push({ name: "home" }).then()
    return false
  }

  return true
}

export const ensureHasServiceRequestingFeature: NavigationGuard = (_to, _from, _next) => {
  const hasServiceRequestingFeature = useSubscriptionStore().hasServiceRequestingFeature
  if (!hasServiceRequestingFeature) {
    router.push({ name: "home" }).then()
    return false
  }

  return true
}

export const ensureHasClinicsFeature: NavigationGuard = (_to, _from, _next) => {
  const hasClinicsFeature = useSubscriptionStore().hasClinicPlanIn
  if (!hasClinicsFeature) {
    router.push({ name: "home" }).then()
    return false
  }

  return true
}

export const ensureHasYouthClinicsFeature: NavigationGuard = (_to, _from, _next) => {
  const hasYouthClinicsFeature = useSubscriptionStore().hasYouthClinicsIn
  if (!hasYouthClinicsFeature) {
    router.push({ name: "home" }).then()
    return false
  }

  return true
}

export const ensureHasOccHealthFeature: NavigationGuard = (_to, _from, _next) => {
  const hasOccHealthFeature = useSubscriptionStore().hasOccHealthPlanIn
  if (!hasOccHealthFeature) {
    router.push({ name: "home" }).then()
    return false
  }

  return true
}

export const ensureEmbedded = createEnsureEmbedded(() => router)
export const ensureNotEmbedded = createEnsureNotEmbedded(() => router, "embed-employer-benefits")
export { ensureEmbedAuthenticated }
